import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { always } from 'ramda';

import { logger } from '#src/lib/logger';
import { useCreateNotification } from '#src/hooks';

const query = gql`
  mutation AddOrderItem($orderId: PositiveInt!, $item: OrderItemInput!) {
    addItem: addOrderItemV2(orderId: $orderId, item: $item) {
      __typename
      id
      amountDue

      orderItems {
        id
        type
        amount

        ... on GameOrderItem {
          id
          paymentPlan
        }
        ... on MembershipOrderItem {
          id
          paymentPlan
        }
      }

      requiresPaymentMethod
      requiresStoreInVault
    }
  }
`;

export function useSelectPaymentPlan({
  requiresPaymentMethod,
  requiresStoreInVault,
  orderId,
  orderItems,
  amountDue,
}) {
  const [mutate] = useMutation(query);
  const createNotification = useCreateNotification();

  const selectPaymentPlan = !orderItems
    ? always(false)
    : ({
        amount,
        code: paymentPlan,
        gameId,
        gameType,
        membershipOfferId,
        membershipType,
        type,
      }) => {
        try {
          let updatedAmountDue = amountDue;
          const updatedOrderItems = orderItems.map((item) => {
            if (item.type == 'GameOrderItem' && item.game?.id == gameId) {
              // remove current plan amount
              updatedAmountDue -= item.amount;
              // add new plan amount
              updatedAmountDue += amount;

              return { ...item, amount, paymentPlan };
            }

            if (item.type === 'MembershipOrderItem') {
              return {
                ...item,
                selectedPaymentPlan:
                  item.paymentPlanChoices?.find?.(
                    ({ code }) => code === paymentPlan
                  ) || item.selectedPaymentPlan,
              };
            }
            return item;
          });

          const byType = {
            MembershipOrderItem: {
              Membership: {
                gameType,
                paymentPlan,
                membershipOfferId,
                membershipType,
              },
            },
            GameOrderItem: {
              Game: {
                gameId,
                gameType,
                paymentPlan,
              },
            },
          };

          const item = byType[type] || byType.GameOrderItem;

          return mutate({
            variables: {
              orderId,
              item,
            },
            optimisticResponse: {
              addItem: {
                __typename: 'Order',
                id: orderId,
                amountDue: updatedAmountDue,
                orderItems: updatedOrderItems,
                requiresStoreInVault,
                requiresPaymentMethod,
              },
            },
          });
        } catch (error) {
          const message = 'Could not change the payment plan.';

          logger.error(error, { message });
          createNotification({ message, status: 'error' });
        }
      };

  return { selectPaymentPlan };
}
